import { APIEndpoints } from '@/@admin/types/constants';
import { AdminToolsParams, UpdateResponse, User } from '@/@admin/types/entities';
import { buildCacheKey } from '@/@admin/utils';
import { ERROR_MESSAGES } from '@/@cms/types';
import { tracking } from '@/lib/features/tracking';
import { portalApi } from '@/lib/http/axios';
import { BaseService } from './base.service';

/**
 * Service class for user management
 * Uses the Portal API (mfour-studio-api) for admin user operations
 */
export class UserService extends BaseService {
    constructor() {
        super(portalApi);
    }

    async getUsers(params: AdminToolsParams): Promise<User[]> {
        try {
            const cacheKey = buildCacheKey('adminFetchUsers', params);
            const cachedData = this.getCachedData(cacheKey);
            if (cachedData) return cachedData;

            const { data: response } = await this.axiosInstance.get<{
                hasError: boolean;
                message?: string;
                data?: User[];
            }>(`${APIEndpoints.USERS}/all`, { params });

            // Ensure we have data
            if (response?.hasError || !Array.isArray(response?.data)) {
                const msg = response?.message ?? 'No users data received from the server';
                tracking.errorWithSource(new Error(msg), 'studio.UserService.getUsers');
                throw new Error(ERROR_MESSAGES.API_ERROR(msg));
            }

            // Cache the response data
            this.setCachedData(cacheKey, response.data);
            return response.data;
        } catch (error) {
            // Enhance error for better debugging
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            tracking.errorWithSource(
                new Error(`Failed to get users: ${errorMessage}`),
                'studio.UserService.getUsers',
            );
            throw new Error(ERROR_MESSAGES.API_ERROR(`Failed to get users: ${errorMessage}`));
        }
    }

    async updateUser(userId: number, data: Partial<User>): Promise<UpdateResponse> {
        try {
            const { data: response } = await this.axiosInstance.patch(
                `${APIEndpoints.USERS}/${userId}`,
                data,
            );

            // Ensure we have a valid response
            if (response.hasError) {
                tracking.errorWithSource(
                    new Error(response.message || 'User could not be updated.'),
                    'studio.UserService.updateUser',
                );
                throw new Error('User could not be updated.');
            }

            return response;
        } catch (error) {
            // Enhance error for better debugging
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            tracking.errorWithSource(
                new Error(`Failed to update user: ${errorMessage}`),
                'studio.UserService.updateUser',
            );
            throw new Error(ERROR_MESSAGES.API_ERROR(`Failed to update user: ${errorMessage}`));
        }
    }

    async verifyUserEmail(email: string, setStatusToActive = true): Promise<{ message: string }> {
        try {
            const { data: response } = await this.axiosInstance.post<{ message: string }>(
                APIEndpoints.ADMIN_VERIFY_EMAIL,
                { email: email.toLowerCase(), setStatusToActive },
            );
            return response;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            tracking.errorWithSource(
                new Error(`Failed to verify user email: ${errorMessage}`),
                'studio.UserService.verifyUserEmail',
            );
            throw new Error(
                ERROR_MESSAGES.API_ERROR(`Failed to verify user email: ${errorMessage}`),
            );
        }
    }

    async changeAssociatedClient(
        orgUserId: number,
        newOrgId: number,
    ): Promise<{ message: string }> {
        try {
            const { data: response } = await this.axiosInstance.post<{ message: string }>(
                APIEndpoints.ADMIN_CHANGE_ASSOCIATED_CLIENT,
                { orgUserId, newOrgId },
            );
            this.clearCache();
            return response;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            tracking.errorWithSource(
                new Error(`Failed to change associated client: ${errorMessage}`),
                'studio.UserService.changeAssociatedClient',
            );
            throw new Error(
                ERROR_MESSAGES.API_ERROR(`Failed to change associated client: ${errorMessage}`),
            );
        }
    }

    async setUserTemporaryPassword(email: string): Promise<{ message: string }> {
        try {
            const { data: response } = await this.axiosInstance.post<{ message: string }>(
                APIEndpoints.AUTH_SET_TEMP_PASSWORD,
                { email: email.toLowerCase() },
            );
            return response;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            tracking.errorWithSource(
                new Error(`Failed to set temporary password: ${errorMessage}`),
                'studio.UserService.setUserTemporaryPassword',
            );
            throw new Error(
                ERROR_MESSAGES.API_ERROR(`Failed to set temporary password: ${errorMessage}`),
            );
        }
    }
}

export const userService = new UserService();
